<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useBookingsStore, type Booking } from '../stores/bookings'
import { api } from '../api'

const authStore = useAuthStore()
const bookingsStore = useBookingsStore()

interface User {
  id: string
  email: string
  name: string
  role: string
  can_book: boolean
  can_approve: boolean
  created_at: string
  last_login: string | null
}

interface Invite {
  code: string
  role: string
  can_book: boolean
  max_uses: number
  uses: number
  expires_at: string
  created_by_name: string
}

const users = ref<User[]>([])
const invites = ref<Invite[]>([])
const loading = ref(true)
const activeTab = ref<'requests' | 'users' | 'invites'>('requests')

// Invite form
const inviteForm = ref({
  role: 'friend',
  can_book: false,
  max_uses: 1,
  expires_days: 7,
})
const newInviteCode = ref('')

onMounted(async () => {
  await Promise.all([
    bookingsStore.fetchBookings({ status: 'pending' }),
    loadUsers(),
    loadInvites(),
  ])
  loading.value = false
})

async function loadUsers() {
  try {
    const response = await api.get('/api/users')
    users.value = response.users
  } catch (error) {
    console.error('Failed to load users:', error)
  }
}

async function loadInvites() {
  try {
    const response = await api.get('/api/users/invites')
    invites.value = response.invites
  } catch (error) {
    console.error('Failed to load invites:', error)
  }
}

async function approveBooking(id: string) {
  await bookingsStore.updateBooking(id, { status: 'approved' })
}

async function rejectBooking(id: string) {
  if (confirm('Are you sure you want to reject this booking request?')) {
    await bookingsStore.updateBooking(id, { status: 'rejected' })
  }
}

async function updateUserRole(userId: string, role: string) {
  await api.patch(`/api/users/${userId}`, { role })
  await loadUsers()
}

async function toggleCanBook(user: User) {
  await api.patch(`/api/users/${user.id}`, { can_book: !user.can_book })
  await loadUsers()
}

async function deleteUser(userId: string) {
  if (confirm('Are you sure you want to remove this user?')) {
    await api.delete(`/api/users/${userId}`)
    await loadUsers()
  }
}

async function createInvite() {
  try {
    const response = await api.post('/api/users/invites', inviteForm.value)
    newInviteCode.value = response.invite.code
    await loadInvites()
  } catch (error) {
    alert('Failed to create invite')
  }
}

async function deleteInvite(code: string) {
  if (confirm('Are you sure you want to revoke this invite code?')) {
    await api.delete(`/api/users/invites/${code}`)
    await loadInvites()
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleDateString()
}
</script>

<template>
  <div class="max-w-6xl mx-auto p-4 md:p-6">
    <h1 class="text-2xl font-bold text-gray-800 mb-6">Admin Panel</h1>

    <!-- Tabs -->
    <div class="flex gap-2 mb-6 border-b">
      <button
        @click="activeTab = 'requests'"
        :class="[
          'px-4 py-2 font-medium -mb-px',
          activeTab === 'requests'
            ? 'border-b-2 border-primary-600 text-primary-600'
            : 'text-gray-500 hover:text-gray-700'
        ]"
      >
        Pending Requests
        <span
          v-if="bookingsStore.pendingBookings.length"
          class="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full"
        >
          {{ bookingsStore.pendingBookings.length }}
        </span>
      </button>
      <button
        @click="activeTab = 'users'"
        :class="[
          'px-4 py-2 font-medium -mb-px',
          activeTab === 'users'
            ? 'border-b-2 border-primary-600 text-primary-600'
            : 'text-gray-500 hover:text-gray-700'
        ]"
      >
        Users
      </button>
      <button
        @click="activeTab = 'invites'"
        :class="[
          'px-4 py-2 font-medium -mb-px',
          activeTab === 'invites'
            ? 'border-b-2 border-primary-600 text-primary-600'
            : 'text-gray-500 hover:text-gray-700'
        ]"
      >
        Invite Codes
      </button>
    </div>

    <!-- Pending Requests -->
    <div v-if="activeTab === 'requests'" class="space-y-4">
      <div v-if="bookingsStore.pendingBookings.length === 0" class="card text-center py-12">
        <div class="text-5xl mb-4">✅</div>
        <p class="text-gray-600">No pending requests</p>
      </div>

      <div
        v-for="booking in bookingsStore.pendingBookings"
        :key="booking.id"
        class="card"
      >
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 class="font-bold text-gray-800">{{ booking.title || `${booking.user_name}'s stay` }}</h3>
            <p class="text-gray-600">
              {{ booking.check_in }} to {{ booking.check_out }}
              ({{ booking.num_guests }} guest{{ booking.num_guests > 1 ? 's' : '' }})
            </p>
            <p class="text-sm text-gray-500">
              Requested by {{ booking.user_name }} ({{ booking.user_email }})
            </p>
            <p v-if="booking.notes" class="text-sm text-gray-600 mt-2">
              <strong>Notes:</strong> {{ booking.notes }}
            </p>
          </div>
          <div class="flex gap-2">
            <button @click="approveBooking(booking.id)" class="btn btn-success">
              Approve
            </button>
            <button @click="rejectBooking(booking.id)" class="btn btn-danger">
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Users -->
    <div v-if="activeTab === 'users'" class="card overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b">
            <th class="text-left py-3 px-4">User</th>
            <th class="text-left py-3 px-4">Role</th>
            <th class="text-left py-3 px-4">Can Book</th>
            <th class="text-left py-3 px-4">Last Login</th>
            <th class="text-left py-3 px-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id" class="border-b">
            <td class="py-3 px-4">
              <div class="font-medium">{{ user.name }}</div>
              <div class="text-sm text-gray-500">{{ user.email }}</div>
            </td>
            <td class="py-3 px-4">
              <select
                :value="user.role"
                @change="updateUserRole(user.id, ($event.target as HTMLSelectElement).value)"
                class="form-input py-1 text-sm"
                :disabled="user.id === authStore.user?.id"
              >
                <option value="owner">Owner</option>
                <option value="property_manager">Property Manager</option>
                <option value="family">Family</option>
                <option value="friend">Friend</option>
                <option value="guest">Guest</option>
              </select>
            </td>
            <td class="py-3 px-4">
              <input
                type="checkbox"
                :checked="user.can_book"
                @change="toggleCanBook(user)"
                :disabled="user.role === 'owner'"
                class="w-4 h-4"
              />
            </td>
            <td class="py-3 px-4 text-sm text-gray-500">
              {{ formatDate(user.last_login) }}
            </td>
            <td class="py-3 px-4">
              <button
                v-if="user.id !== authStore.user?.id"
                @click="deleteUser(user.id)"
                class="text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Invite Codes -->
    <div v-if="activeTab === 'invites'" class="space-y-6">
      <!-- Create Invite Form -->
      <div class="card">
        <h3 class="font-bold text-gray-800 mb-4">Create Invite Code</h3>

        <div v-if="newInviteCode" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p class="text-sm text-green-700 mb-2">New invite code created:</p>
          <p class="text-2xl font-mono font-bold text-green-800 tracking-widest">{{ newInviteCode }}</p>
          <button @click="newInviteCode = ''" class="text-sm text-green-600 mt-2">
            Dismiss
          </button>
        </div>

        <form @submit.prevent="createInvite" class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="form-label">Role</label>
            <select v-model="inviteForm.role" class="form-input">
              <option value="property_manager">Property Manager</option>
              <option value="family">Family</option>
              <option value="friend">Friend</option>
              <option value="guest">Guest (view only)</option>
            </select>
          </div>
          <div>
            <label class="form-label">Can Book Directly</label>
            <select v-model="inviteForm.can_book" class="form-input">
              <option :value="false">No (requires approval)</option>
              <option :value="true">Yes</option>
            </select>
          </div>
          <div>
            <label class="form-label">Max Uses</label>
            <input v-model.number="inviteForm.max_uses" type="number" min="1" class="form-input" />
          </div>
          <div class="flex items-end">
            <button type="submit" class="btn btn-primary w-full">
              Create Code
            </button>
          </div>
        </form>
      </div>

      <!-- Existing Invites -->
      <div class="card">
        <h3 class="font-bold text-gray-800 mb-4">Active Invite Codes</h3>

        <div v-if="invites.length === 0" class="text-center py-8 text-gray-500">
          No invite codes created yet
        </div>

        <table v-else class="w-full">
          <thead>
            <tr class="border-b">
              <th class="text-left py-3 px-4">Code</th>
              <th class="text-left py-3 px-4">Role</th>
              <th class="text-left py-3 px-4">Uses</th>
              <th class="text-left py-3 px-4">Expires</th>
              <th class="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invite in invites" :key="invite.code" class="border-b">
              <td class="py-3 px-4 font-mono font-bold">{{ invite.code }}</td>
              <td class="py-3 px-4">{{ invite.role }}</td>
              <td class="py-3 px-4">{{ invite.uses }} / {{ invite.max_uses }}</td>
              <td class="py-3 px-4 text-sm">{{ formatDate(invite.expires_at) }}</td>
              <td class="py-3 px-4">
                <button
                  @click="deleteInvite(invite.code)"
                  class="text-red-600 hover:text-red-800 text-sm"
                >
                  Revoke
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
